import API_BASE, { appwriteFetch } from "./apiBase";

const AUTH_STORAGE_KEY = "authUser";
const AUTH_CHANGED_EVENT = "auth:changed";
let logoutInFlight = null;

function parseJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function getAuthUser() {
  return parseJson(localStorage.getItem(AUTH_STORAGE_KEY));
}

export function isAuthenticated() {
  const user = getAuthUser();
  return Boolean(user?.token);
}

export function saveAuthUser(user) {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function clearAuthUser() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function getAuthToken() {
  return getAuthUser()?.token || "";
}

const JOI_FRIENDLY = {
  "string.pattern.base": {
    password: "Password must be 8–15 characters and include at least one uppercase letter, one lowercase letter, and one special character (@#$%^&+=).",
  },
  "string.min": (d) => `${capitalize(d.context?.label)} must be at least ${d.context?.limit} characters.`,
  "string.max": (d) => `${capitalize(d.context?.label)} must be at most ${d.context?.limit} characters.`,
  "string.email": () => "Please enter a valid email address.",
  "any.required": (d) => `${capitalize(d.context?.label)} is required.`,
  "string.empty": (d) => `${capitalize(d.context?.label)} cannot be empty.`,
};

function capitalize(str) {
  if (!str) return "Field";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function friendlyJoiMessage(detail) {
  const type = detail?.type;
  const field = detail?.context?.key || detail?.path?.[0] || "";
  const entry = JOI_FRIENDLY[type];
  if (!entry) return detail.message?.replace(/["']/g, "") || "";
  if (typeof entry === "function") return entry(detail);
  if (typeof entry === "object") return entry[field] || detail.message?.replace(/["']/g, "") || "";
  return entry;
}

async function parseResponse(response) {
  const text = await response.text();
  let payload = null;

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!response.ok) {
    let errorMessage = payload?.message || "Request failed.";

    // Parse Joi validation errors: "Invalid Request: {\"details\":[...]}"
    if (typeof errorMessage === "string" && errorMessage.startsWith("Invalid Request:")) {
      try {
        const jsonPart = errorMessage.replace(/^Invalid Request:\s*/, "");
        const parsed = JSON.parse(jsonPart);
        const details = parsed?.details;
        if (Array.isArray(details) && details.length > 0) {
          errorMessage = details
            .map((d) => friendlyJoiMessage(d))
            .filter(Boolean)
            .join("\n");
        }
      } catch {
        // fallback to raw message
      }
    }

    throw new Error(errorMessage);
  }

  return payload || {};
}

export async function loginUser({ email, password }) {
  const response = await appwriteFetch(`${API_BASE}/user/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password, setCookie: true }),
  });

  const payload = await parseResponse(response);
  const user = {
    firstName: payload.firstName || "",
    lastName: payload.lastName || "",
    email,
    token: payload.token || "",
  };

  saveAuthUser(user);
  return user;
}

export async function registerUser({ firstName, lastName, email, password }) {
  const response = await appwriteFetch(`${API_BASE}/user/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ firstName, lastName, email, password, setCookie: true }),
  });

  const payload = await parseResponse(response);
  const user = {
    firstName: payload.firstName || firstName || "",
    lastName: payload.lastName || lastName || "",
    email,
    token: payload.token || "",
  };

  saveAuthUser(user);
  return user;
}

export async function logoutUser() {
  if (logoutInFlight) return logoutInFlight;

  logoutInFlight = (async () => {
    try {
      await appwriteFetch(`${API_BASE}/user/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch {
      // Intentionally ignore network/logout endpoint failures and clear local auth state.
    } finally {
      clearAuthUser();
    }
  })();

  try {
    await logoutInFlight;
  } finally {
    logoutInFlight = null;
  }
}

export async function requestPasswordOtp(email) {
  const response = await appwriteFetch(`${API_BASE}/user/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });

  return parseResponse(response);
}

export async function verifyPasswordOtp({ email, otp }) {
  const response = await appwriteFetch(`${API_BASE}/user/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, otp }),
  });

  const payload = await parseResponse(response);
  const message = payload?.message || "";
  if (/expired/i.test(message)) {
    throw new Error("OTP has expired. Please request a new one.");
  }

  return payload;
}

export async function resetUserPassword({ email, password, confirmPassword }) {
  const response = await appwriteFetch(`${API_BASE}/user/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password, confirmPassword }),
  });

  return parseResponse(response);
}

export async function saveResumeFormData({ formData, templateId }) {
  const token = getAuthToken();
  if (!token) return;

  try {
    const response = await appwriteFetch(`${API_BASE}/api/user-form-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json", token },
      credentials: "include",
      body: JSON.stringify({ formData, templateId }),
    });
    return await parseResponse(response);
  } catch {
    // Silent fail — localStorage still holds the data
  }
}

export async function loadResumeFormData() {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const response = await appwriteFetch(`${API_BASE}/api/user-form-data`, {
      method: "GET",
      headers: { "Content-Type": "application/json", token },
      credentials: "include",
    });

    if (response.status === 404) return null;
    return await parseResponse(response);
  } catch {
    return null;
  }
}

export const authEvents = {
  AUTH_CHANGED_EVENT,
};
